"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { cfImage, rawImage } from "@/lib/img";

// Card photo with a transform→raw fallback. Cloudflare image transforms are
// metered (5,000 unique/month free); once that's spent a NEW transform 429s and
// the card would show a broken icon. On error we swap to the raw pre-made WebP
// (always 200s straight from R2). The swap is done via STATE, not by mutating
// img.src in the DOM: next/image controls the `src` prop, so a raw DOM swap gets
// clobbered on the next render — setState re-renders <Image> with the new src so
// it sticks. rawImage() is a no-op on an already-raw URL, so the guard
// (raw !== src) makes this step transform→raw exactly once and never loop.
function CardImage({
  url,
  alt,
  unavailable,
  extraClass = "",
}: {
  url: string;
  alt: string;
  unavailable?: boolean;
  extraClass?: string;
}) {
  const [src, setSrc] = useState(() => cfImage(url, { width: 800 }));
  const imgRef = useRef<HTMLImageElement | null>(null);
  const toRaw = () => {
    const raw = rawImage(src);
    if (raw !== src) setSrc(raw);
  };
  // `onError` alone is NOT sufficient: React attaches the listener at
  // hydration, so an image that already failed before hydration fires its
  // error event into the void and keeps a broken src forever. The eager cover
  // on a server-rendered grid hits exactly that window. Re-check the real DOM
  // node once per src — `complete && naturalWidth === 0` is the definitive
  // "this failed" signal — so a missing derivative always degrades to the raw
  // original instead of a blank tile (the L-103 symptom).
  useEffect(() => {
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth === 0) toRaw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
  return (
    <Image
      ref={imgRef}
      src={src}
      alt={alt}
      fill
      sizes={CARD_SIZES}
      // next/image defaults to lazy; keep it explicit — a grid of ~16 cards must
      // not eager-decode the full-res R2 originals (images.unoptimized=true).
      loading="lazy"
      onError={toRaw}
      className={`object-cover ${unavailable ? "grayscale" : ""} ${extraClass}`}
    />
  );
}

// Fallback labels used when this component is rendered OUTSIDE a
// NextIntlClientProvider — e.g. the /boutique route segment, which is
// brand-isolated and intentionally English-only (no next-intl provider in
// its layout tree). Before this guard, calling `useTranslations` on the
// boutique homepage threw `Error(undefined)` during prerender and aborted
// the Vercel build (see 4d7f7b3 → e88ccda regression chain).
const FALLBACK_LABELS = {
  noPhoto: "No photo available",
  prevPhoto: "Previous photo",
  nextPhoto: "Next photo",
};

// Safe translator that returns fallback strings when no NextIntlClientProvider
// is mounted. next-intl's `useTranslations` re-throws as a bare `Error()` when
// the provider is missing, with no message and no stack, which is what was
// crashing /boutique prerender.
function useListingCardLabels(): (key: keyof typeof FALLBACK_LABELS) => string {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const t = useTranslations("listingCard");
    return (key) => {
      try {
        return t(key);
      } catch {
        return FALLBACK_LABELS[key];
      }
    };
  } catch {
    return (key) => FALLBACK_LABELS[key];
  }
}

// Card images sit in a responsive grid (1 col on phones → ~3-4 on desktop).
// This tells the browser/optimizer to fetch a small image on mobile instead
// of the full-resolution R2 original.
const CARD_SIZES = "(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 30vw";

/**
 * Swipeable photo carousel for listing cards (homepage grid, /search grid).
 *
 * Why cards didn't have this before: ListingCard rendered a single static
 * `cover` image. Browsing felt flat next to Airbnb/Booking, where every
 * card lets you flick through photos without opening the listing.
 *
 * Implementation notes:
 *  - Native scroll-snap drives the swipe — works on touch with zero JS,
 *    and the arrows just call scrollTo for desktop.
 *  - The card is wrapped in a <Link>; the arrow buttons call
 *    preventDefault + stopPropagation so a click paginates instead of
 *    navigating. Swiping the image still lets a tap-through navigate.
 *  - Capped at 6 photos per card — enough to entice, cheap to load.
 *    Photos 2-6 are lazy so a grid of 18 cards doesn't fetch 100+ images
 *    up front.
 */

const MAX_CARD_PHOTOS = 6;

export default function ListingCardGallery({
  photos,
  alt,
  unavailable,
}: {
  photos: string[];
  alt: string;
  unavailable?: boolean;
}) {
  const t = useListingCardLabels();
  const slides = (photos ?? []).filter(Boolean).slice(0, MAX_CARD_PHOTOS);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  // Only the cover is mounted until the visitor touches THIS card.
  //
  // The slides sit in a horizontal scroller, and the browser's lazy-load margin
  // is applied on both axes — so slides 2 and 3 of every card loaded even
  // though nobody had swiped: measured 3 decoded photos per card, ~6 MB each
  // card, 104 MB for a single 20-card page on a phone (2026-09-20). Mounting
  // them on first interaction cuts that to the cover alone; the photo request
  // starts on touchstart, ahead of the swipe that reveals it.
  const [primed, setPrimed] = useState(false);
  const prime = () => setPrimed(true);

  // Fallback — a single static image, no carousel chrome.
  if (slides.length <= 1) {
    return (
      <div className="relative w-full h-full">
        {slides[0] ? (
          <CardImage
            url={slides[0]}
            alt={alt}
            unavailable={unavailable}
            extraClass="transition-transform duration-500 group-hover:scale-[1.06]"
          />
        ) : (
          <div className="w-full h-full bg-ss-bg-soft flex items-center justify-center text-ss-soft text-sm">
            {t("noPhoto")}
          </div>
        )}
      </div>
    );
  }

  function scrollToIndex(i: number) {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(i, slides.length - 1));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
    setActive(clamped);
  }

  function onScroll() {
    const el = scrollerRef.current;
    if (!el) return;
    if (!primed) setPrimed(true);
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== active) setActive(i);
  }

  // Arrow handler — stop the click from bubbling to the wrapping <Link>.
  function arrow(e: React.MouseEvent, dir: -1 | 1) {
    e.preventDefault();
    e.stopPropagation();
    prime(); // arrows sit outside the scroller, so they prime the slides too
    scrollToIndex(active + dir);
  }

  return (
    <div className="relative w-full h-full">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        // Touch starts the fetch before the swipe lands; pointerenter covers
        // desktop hover (where the arrows appear anyway).
        onTouchStart={prime}
        onPointerDown={prime}
        onPointerEnter={prime}
        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-none scroll-smooth"
      >
        {slides.map((src, i) => (
          <div key={i} className="relative flex-none w-full h-full snap-center">
            {i === 0 || primed ? (
              <CardImage
                url={src}
                // Every slide gets descriptive alt (not just the cover) — empty
                // alt on photos 2+ was flagged "missing alt" across every card on
                // home/search/compounds and wastes image-SEO signal.
                alt={i === 0 ? alt : `${alt} — photo ${i + 1}`}
                unavailable={unavailable}
              />
            ) : (
              /* Placeholder keeps the slide's width so scroll-snap, the dots
                 and the swipe gesture behave exactly as before. */
              <div className="w-full h-full bg-ss-bg-soft" aria-hidden="true" />
            )}
          </div>
        ))}
      </div>

      {/* Prev / next — desktop only (touch users swipe). Fade in on card hover. */}
      {active > 0 && (
        <button
          type="button"
          aria-label={t("prevPhoto")}
          onClick={(e) => arrow(e, -1)}
          className="hidden md:grid absolute start-2 top-1/2 -translate-y-1/2 w-7 h-7 place-items-center rounded-full bg-white/90 text-ss-text shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
        >
          <span className="rtl:-scale-x-100 inline-block leading-none">‹</span>
        </button>
      )}
      {active < slides.length - 1 && (
        <button
          type="button"
          aria-label={t("nextPhoto")}
          onClick={(e) => arrow(e, 1)}
          className="hidden md:grid absolute end-2 top-1/2 -translate-y-1/2 w-7 h-7 place-items-center rounded-full bg-white/90 text-ss-text shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
        >
          <span className="rtl:-scale-x-100 inline-block leading-none">›</span>
        </button>
      )}

      {/* Dot indicators */}
      <div className="absolute bottom-2 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 flex gap-1">
        {slides.map((_, i) => (
          <span
            key={i}
            className={`rounded-full transition-all ${
              i === active ? "w-2 h-2 bg-white" : "w-1.5 h-1.5 bg-white/55"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
